import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { supabase } from './supabase'

export type BreadcrumbItem = {
    href?: string
    label: string
    current?: boolean
    icon?: boolean
}

export function useBreadcrumbs() {
    const location = useLocation()
    const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([])
    const [error, setError] = useState<Error | null>(null)

    useEffect(() => {
        let mounted = true

        async function generateBreadcrumbs() {
            try {
                const pathSegments = location.pathname.split('/').filter(Boolean)

                // Always start with home
                const crumbs: BreadcrumbItem[] = [{
                    href: '/dashboard',
                    label: 'Home',
                    icon: true
                }]

                // Map known paths to their display names
                const pathDisplayNames: { [key: string]: string } = {
                    'campaigns': 'Campaigns',
                    'content': 'Content',
                    'convergences': 'Convergences',
                    'brand-genius': 'Brand Genius',
                    'settings': 'Settings',
                    'admin': 'Admin',
                    'help': 'Help',
                    'connections': 'Connections'
                }

                // Check if we're on a connection-related page to handle special breadcrumb logic
                const isConnectionPage = pathSegments.length >= 3 &&
                    pathSegments[0] === 'brands' &&
                    pathSegments[2] === 'connections'

                // Process each path segment
                for (let i = 0; i < pathSegments.length; i++) {
                    const segment = pathSegments[i]
                    const fullPath = `/${pathSegments.slice(0, i + 1).join('/')}`

                    // Skip the first segment if it's 'brands' - we'll handle brands specially
                    if (i === 0 && segment === 'brands') {
                        continue
                    }

                    // Handle brand UUID specifically (should be index 1 after 'brands')
                    if (i === 1 && pathSegments[0] === 'brands') {
                        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
                        if (uuidRegex.test(segment)) {
                            try {
                                const { data: brandData, error: brandError } = await supabase
                                    .from('brands')
                                    .select('brand_name')
                                    .eq('id', segment)
                                    .single()

                                let brandName = segment // fallback
                                if (!brandError && brandData?.brand_name) {
                                    brandName = brandData.brand_name
                                }

                                crumbs.push({
                                    href: fullPath,
                                    label: brandName,
                                    current: i === pathSegments.length - 1
                                })
                                continue
                            } catch (error) {
                                console.error('Error fetching brand name:', error)
                            }
                        }
                    }

                    // Special handling for connection pages
                    if (isConnectionPage && segment === 'connections') {
                        // For connection pages, replace "Connections" with "Social Automation Agents"
                        // and skip any UUID that follows (for settings pages)
                        if (i + 1 < pathSegments.length && pathSegments[i + 1] !== 'settings') {
                            // This is a UUID segment, skip it
                            i++ // Skip the UUID segment that follows
                        }
                        crumbs.push({
                            href: `/${pathSegments.slice(0, 3).join('/')}`, // /brands/<brandId>/connections
                            label: 'Social Automation Agents',
                            current: i === pathSegments.length - 1
                        })
                        continue
                    }

                    // If it's a known path, use its display name
                    if (pathDisplayNames[segment]) {
                        crumbs.push({
                            href: fullPath,
                            label: pathDisplayNames[segment],
                            current: i === pathSegments.length - 1
                        })
                        continue
                    }

                    // Check if it's a UUID - if so, fetch the entity name
                    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
                    if (uuidRegex.test(segment)) {
                        let entityName = segment // fallback
                        const entityType = pathSegments[i - 1]

                        if (entityType) {
                            try {
                                let data
                                switch (entityType) {
                                    case 'campaigns':
                                        const { data: campaignData, error: campaignError } = await supabase
                                            .from('campaigns')
                                            .select('name')
                                            .eq('id', segment)
                                            .single()
                                        if (campaignError) throw campaignError
                                        data = campaignData
                                        break

                                    case 'content':
                                        const { data: contentData, error: contentError } = await supabase
                                            .from('current_content_versions')
                                            .select('name')
                                            .eq('id', segment)
                                            .single()
                                        if (contentError) throw contentError
                                        data = contentData
                                        break

                                    case 'convergences':
                                        const { data: convergenceData, error: convergenceError } = await supabase
                                            .from('convergences')
                                            .select('name, trend')
                                            .eq('id', segment)
                                            .single()

                                        if (convergenceError) throw convergenceError
                                        if (convergenceData) {
                                            entityName = convergenceData.name || convergenceData.trend
                                        }
                                        data = convergenceData
                                        break

                                    case 'brands':
                                        const { data: brandData, error: brandError } = await supabase
                                            .from('brands')
                                            .select('brand_name')
                                            .eq('id', segment)
                                            .single()
                                        if (brandError) throw brandError
                                        if (brandData) {
                                            entityName = brandData.brand_name
                                        }
                                        break

                                    case 'connections':
                                        // For connections, try to get platform connection info from platform_brand_settings
                                        const { data: connectionData, error: connectionError } = await supabase
                                            .from('platform_brand_settings')
                                            .select(`
                                                connection_details,
                                                platforms!platform_brand_settings_platform_id_fkey (
                                                    name,
                                                    platform_key
                                                )
                                            `)
                                            .eq('id', segment)
                                            .single()

                                        // Log PGRST116 errors at debug level (record not found)
                                        if (connectionError && connectionError.code === 'PGRST116') {
                                            console.debug('Platform connection record not found for breadcrumb:', segment)
                                        } else if (connectionError) {
                                            console.error('Error fetching platform connection for breadcrumb:', connectionError)
                                        }

                                        if (!connectionError && connectionData) {
                                            const platform = connectionData.platforms?.name || connectionData.platforms?.platform_key
                                            const connectionDetails = connectionData.connection_details as any

                                            let displayName = platform

                                            // Try to extract meaningful info from connection_details
                                            if (connectionDetails) {
                                                const username = connectionDetails.username ||
                                                    connectionDetails.screen_name ||
                                                    connectionDetails.account_username
                                                const name = connectionDetails.name ||
                                                    connectionDetails.display_name ||
                                                    connectionDetails.account_name

                                                if (username) {
                                                    displayName = `${platform} (@${username})`
                                                } else if (name) {
                                                    displayName = `${platform} (${name})`
                                                }
                                            }

                                            entityName = displayName || 'Connection'
                                        } else {
                                            // Fallback: show truncated UUID
                                            entityName = segment.substring(0, 8) + '...'
                                        }
                                        break
                                }

                                if (data?.name || (data as any)?.brand_name) {
                                    entityName = (data as any)?.brand_name || data?.name
                                }
                                // Don't override entityName for connections since it's already properly formatted above
                            } catch (error) {
                                console.error('Error fetching entity name:', error)
                                setError(error as Error)
                            }
                        }

                        crumbs.push({
                            href: fullPath,
                            label: entityName,
                            current: i === pathSegments.length - 1
                        })
                        continue
                    }

                    // For any other segments, just capitalize them
                    crumbs.push({
                        href: fullPath,
                        label: segment.charAt(0).toUpperCase() + segment.slice(1),
                        current: i === pathSegments.length - 1
                    })
                }

                if (mounted) {
                    setBreadcrumbs(crumbs)
                }
            } catch (error) {
                console.error('Error in generateBreadcrumbs:', error)
                if (mounted) {
                    setError(error as Error)
                }
            }
        }

        generateBreadcrumbs()

        return () => {
            mounted = false
        }
    }, [location.pathname])

    if (error) {
        console.error('useBreadcrumbs error:', error)
    }

    return breadcrumbs
} 